import api from './index';
import axios, { AxiosError } from 'axios';
import { devLog, devWarn } from '../../utils/devLog';

interface ValidateInvitationResponse {
  isValid: boolean;
  hasAccount?: boolean;
  driver?: {
    id: string;
    name: string;
    email: string;
    companyId: string;
    companyName: string;
  };
}

interface AcceptInvitationResponse {
  success: boolean;
  message: string;
}

/**
 * Validates the format of a UUID token
 * @param token The token to validate
 * @returns True if the token appears to be a valid UUID
 */
const isValidUUID = (token: string): boolean => {
  const uuidRegex =
    /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
  return uuidRegex.test(token);
};

/**
 * Validates an invitation token
 * @param token The invitation token to validate
 * @returns Information about the token validity and associated driver
 */
export const validateInvitation = async (
  token: string
): Promise<ValidateInvitationResponse> => {
  try {
    // Check if token appears to be valid
    if (!token || token.trim() === '') {
      console.error('[invitationApi] Invalid token: Token is empty or null');
      return { isValid: false };
    }

    // Trim any whitespace that might have been added
    const cleanToken = token.trim();

    // Check if token appears to be a valid UUID
    if (!isValidUUID(cleanToken)) {
      devWarn(
        '[invitationApi] Token does not appear to be a valid UUID format'
      );
      // Continue anyway as the backend will validate
    }

    devLog('[invitationApi] Validating invitation token');

    const response = await api.post('/driver-invitations/validate', {
      token: cleanToken,
    });
    devLog('[invitationApi] Validation completed', {
      isValid: response.data?.isValid,
    });
    return response.data;
  } catch (error: unknown) {
    console.error('[invitationApi] Error validating invitation token:', error);

    // Log more details about the error
    if (axios.isAxiosError(error)) {
      const axiosError = error as AxiosError;
      if (axiosError.response) {
        console.error('[invitationApi] Error response:', {
          status: axiosError.response.status,
          data: axiosError.response.data,
          url: axiosError.config?.url,
          method: axiosError.config?.method,
        });
      } else if (axiosError.request) {
        console.error(
          '[invitationApi] No response received:',
          axiosError.request
        );
      } else {
        console.error(
          '[invitationApi] Request setup error:',
          axiosError.message
        );
      }
    }

    return { isValid: false };
  }
};

/**
 * Accepts an invitation and links the user to the driver
 * @param token The invitation token
 * @param auth0Id The Auth0 ID of the user
 * @returns Success status and message
 */
/**
 * Accept as the signed-in login. The server takes who that is from the
 * access token and checks its email is the invited one; it took an Auth0 id
 * from the body, so anyone with the link could accept as anyone.
 */
export const acceptInvitation = async (
  token: string,
  accessToken: string
): Promise<AcceptInvitationResponse> => {
  try {
    // Check if token appears to be valid
    if (!token || token.trim() === '') {
      throw new Error('Invalid token: Token is empty or null');
    }

    // Trim any whitespace that might have been added
    const cleanToken = token.trim();

    devLog('[invitationApi] Accepting invitation');

    const response = await api.post(
      '/driver-invitations/accept',
      { token: cleanToken },
      // Sent here: the token manager may not have this new login yet.
      { headers: { Authorization: `Bearer ${accessToken}` } }
    );

    devLog('[invitationApi] Invitation accepted successfully');
    return response.data;
  } catch (error: unknown) {
    console.error('[invitationApi] Error accepting invitation:', error);

    // Log more details about the error
    if (axios.isAxiosError(error)) {
      const axiosError = error as AxiosError;
      if (axiosError.response) {
        console.error('[invitationApi] Error response:', {
          status: axiosError.response.status,
          data: axiosError.response.data,
          url: axiosError.config?.url,
          method: axiosError.config?.method,
        });
      } else if (axiosError.request) {
        console.error(
          '[invitationApi] No response received:',
          axiosError.request
        );
      } else {
        console.error(
          '[invitationApi] Request setup error:',
          axiosError.message
        );
      }
    }

    throw error;
  }
};
